import { useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle,
  DrawerBody, DrawerFooter,
  Button, Input, Textarea, Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
  Switch,
} from '@vnetwork/ui/primitives';
import { MultiSelect, type MultiSelectOption } from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import { useQuery } from '@tanstack/react-query';
import { dataCentralApi } from '@/api/client';
import {
  useCreateCommissionPolicy,
  useUpdateCommissionPolicy,
  useDepartmentOptions,
  useAffiliateOwnerOptions,
  type CommissionPolicy,
  type CreateCommissionPolicyPayload,
  type UpdateCommissionPolicyPayload,
} from '@/api/commission-policies';
import type { ResponseArray } from '@/types/shared';
import {
  policySchema, defaultPolicyValues, type PolicyFormValues,
} from './policy-form-schema';
import { DateInput } from '@/components/date-input';

/* ── Local option hooks (shared api hooks use name-as-value, not numeric IDs) ── */

interface NumericOption { value: string; label: string }

/** Product groups keyed by numeric product_group_id (commission policy needs the ID). */
function useProductGroupNumericOptions() {
  return useQuery({
    queryKey: ['product-groups', 'numeric-options'],
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<NumericOption[]> => {
      const res = await dataCentralApi.get<ResponseArray<{ product_group_id?: number; product_group_name: string }>>(
        '/product-groups?page=1&page_size=200',
      );
      return (res.data.results ?? [])
        .filter((g) => g.product_group_id != null)
        .map((g) => ({
          value: String(g.product_group_id),
          label: g.product_group_name,
        }));
    },
  });
}

function useAllProductOptions() {
  return useQuery({
    queryKey: ['products', 'all-options'],
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<NumericOption[]> => {
      const res = await dataCentralApi.get<ResponseArray<{ product_id: number; product_name: string }>>(
        '/products?page=1&page_size=500',
      );
      return (res.data.results ?? []).map((p) => ({
        value: String(p.product_id),
        label: p.product_name,
      }));
    },
  });
}

/* ── Props ────────────────────────────────────────────────────────────── */

interface PolicyFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Present → edit mode; absent → create mode. */
  policy?: CommissionPolicy | null;
}

/** Radix Select forbids `value=""` on items — 'everyone' is the sentinel for "no audience filter" (maps to '' in form state / null in the payload). */
const EVERYONE = 'everyone';
const REFERRER_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: EVERYONE, label: 'Everyone (no filter)' },
  { value: 'employee', label: 'Employee' },
  { value: 'customer', label: 'Customer' },
  { value: 'partner', label: 'Partner' },
  { value: 'reseller', label: 'Reseller' },
  { value: 'affiliate', label: 'Affiliate' },
  { value: 'other', label: 'Other' },
];

const SCOPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'default', label: 'Default (all)' },
  { value: 'product_group', label: 'Product Group' },
  { value: 'product', label: 'Product' },
];

/**
 * Slide-over create/edit form for a commission policy. Conforms to @ui Drawer
 * conventions: DrawerContent right-side, Field wrappers for all controls,
 * MultiSelect for multi-pick, Select for single-pick enums.
 */
export function PolicyFormDrawer({ open, onOpenChange, policy }: PolicyFormDrawerProps) {
  const isEdit = !!policy;
  const [submitError, setSubmitError] = useState('');

  const create = useCreateCommissionPolicy();
  const update = useUpdateCommissionPolicy(policy?.policy_id ?? 0);

  const { data: deptOptions = [], isLoading: deptLoading } = useDepartmentOptions();
  const { data: productGroupOptions = [], isLoading: pgLoading } = useProductGroupNumericOptions();
  const { data: productOptions = [], isLoading: productLoading } = useAllProductOptions();

  /* Affiliate owner picker — load first page; search not wired to save form space. */
  const [ownerQuery] = useState('');
  const { data: ownerData, isLoading: ownerLoading } = useAffiliateOwnerOptions(ownerQuery, 1, 50);
  const ownerOptions: MultiSelectOption[] = (ownerData?.options ?? []).map((o) => ({
    value: String(o.value),
    label: o.label,
  }));

  const deptMultiOpts: MultiSelectOption[] = deptOptions.map((d) => ({
    value: String(d.value),
    label: d.label,
  }));

  const {
    register, control, reset, setValue,
    handleSubmit, formState: { errors, isSubmitting },
  } = useForm<PolicyFormValues>({
    resolver: yupResolver(policySchema) as never,
    defaultValues: defaultPolicyValues(),
  });

  const scope = useWatch({ control, name: 'scope' });
  const isGlobal = useWatch({ control, name: 'is_global' });
  const referrerType = useWatch({ control, name: 'referrer_type' });

  /* Sync with policy prop when drawer opens */
  useEffect(() => {
    if (open) {
      setSubmitError('');
      if (policy) {
        reset({
          policy_name: policy.policy_name,
          is_global: policy.is_global ?? false,
          affiliate_ids: policy.is_global
            ? []
            : policy.affiliate_ids ?? (policy.affiliate_id ? [policy.affiliate_id] : []),
          scope: policy.scope,
          commission_percent: policy.commission_percent,
          product_group_id: policy.product_group_id ?? undefined,
          product_id: policy.product_id ?? undefined,
          effective_date: policy.effective_date
            ? policy.effective_date.slice(0, 10)
            : defaultPolicyValues().effective_date,
          expired_date: policy.expired_date
            ? policy.expired_date.slice(0, 10)
            : undefined,
          priority: policy.priority,
          description: policy.description ?? '',
          referrer_type: (policy.referrer_type as PolicyFormValues['referrer_type']) ?? '',
          department_ids: policy.department_ids ?? [],
        });
      } else {
        reset(defaultPolicyValues());
      }
    }
  }, [open, policy, reset]);

  /* Clear department_ids when referrer type changes away from employee */
  useEffect(() => {
    if (referrerType !== 'employee') {
      setValue('department_ids', []);
    }
  }, [referrerType, setValue]);

  /* Clear affiliate_ids when global is switched on */
  useEffect(() => {
    if (isGlobal) {
      setValue('affiliate_ids', []);
    }
  }, [isGlobal, setValue]);

  /* Clear scope-specific ids when scope changes */
  useEffect(() => {
    if (scope === 'default') {
      setValue('product_group_id', undefined);
      setValue('product_id', undefined);
    } else if (scope === 'product_group') {
      setValue('product_id', undefined);
    } else if (scope === 'product') {
      setValue('product_group_id', undefined);
    }
  }, [scope, setValue]);

  const onSubmit = async (data: PolicyFormValues) => {
    setSubmitError('');
    try {
      if (isEdit) {
        const payload: UpdateCommissionPolicyPayload = {
          affiliate_ids: data.is_global ? [] : data.affiliate_ids,
          is_global: data.is_global,
          scope: data.scope,
          product_group_id: data.scope === 'product_group' ? data.product_group_id : undefined,
          product_id: data.scope === 'product' ? data.product_id : undefined,
          policy_name: data.policy_name,
          description: data.description || undefined,
          commission_percent: data.commission_percent,
          effective_date: data.effective_date,
          expired_date: data.expired_date || undefined,
          priority: data.priority,
          update_audience: true,
          referrer_type: data.referrer_type || null,
          department_ids: data.referrer_type === 'employee' ? data.department_ids : [],
        };
        await update.mutateAsync(payload);
      } else {
        const payload: CreateCommissionPolicyPayload = {
          affiliate_ids: data.is_global ? [] : data.affiliate_ids,
          is_global: data.is_global,
          scope: data.scope,
          product_group_id: data.scope === 'product_group' ? data.product_group_id : undefined,
          product_id: data.scope === 'product' ? data.product_id : undefined,
          policy_name: data.policy_name,
          description: data.description || undefined,
          commission_percent: data.commission_percent,
          effective_date: data.effective_date,
          expired_date: data.expired_date || undefined,
          priority: data.priority,
          referrer_type: data.referrer_type || null,
          department_ids: data.referrer_type === 'employee' ? data.department_ids : [],
        };
        await create.mutateAsync(payload);
      }
      onOpenChange(false);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to save policy. Please try again.');
    }
  };

  const optionsLoading = pgLoading || productLoading;
  const isPending = isSubmitting || create.isPending || update.isPending;

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent side="right" size="md">
        <DrawerHeader>
          <DrawerTitle>{isEdit ? 'Edit Commission Policy' : 'Create Commission Policy'}</DrawerTitle>
        </DrawerHeader>

        <DrawerBody>
          <form
            id="policy-form"
            onSubmit={handleSubmit(onSubmit)}
            style={{ display: 'grid', gap: 'var(--vn-space-4)' }}
          >
            <Field label="Policy Name" required error={errors.policy_name?.message}>
              {(fp) => (
                <Input
                  {...fp}
                  {...register('policy_name')}
                  placeholder="Enter policy name"
                  invalid={!!errors.policy_name}
                />
              )}
            </Field>

            {/* Owner block */}
            <Field label="Apply globally" description="When on, this policy matches all affiliates instead of specific ones.">
              {(fp) => (
                <Controller
                  name="is_global"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      {...fp}
                      checked={!!field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
              )}
            </Field>

            {!isGlobal && (
              <Field label="Affiliate Owners" required error={errors.affiliate_ids?.message}>
                {(fp) => (
                  <Controller
                    name="affiliate_ids"
                    control={control}
                    render={({ field }) => (
                      <MultiSelect
                        {...fp}
                        placeholder={ownerLoading ? 'Loading affiliates…' : 'Select affiliates'}
                        options={ownerOptions}
                        value={(field.value ?? []).map(String)}
                        onChange={(vals) => field.onChange(vals.map(Number))}
                        disabled={ownerLoading}
                        invalid={!!errors.affiliate_ids}
                        emptyHint="No affiliates found"
                      />
                    )}
                  />
                )}
              </Field>
            )}

            {/* Audience block */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--vn-space-3)' }}>
              <Field label="Audience Type" error={errors.referrer_type?.message}>
                {(fp) => (
                  <Controller
                    name="referrer_type"
                    control={control}
                    render={({ field }) => (
                      <Select
                        value={field.value || EVERYONE}
                        onValueChange={(v) => field.onChange(v === EVERYONE ? '' : v)}
                      >
                        <SelectTrigger {...fp} invalid={!!errors.referrer_type}>
                          <SelectValue placeholder="Everyone (no filter)" />
                        </SelectTrigger>
                        <SelectContent>
                          {REFERRER_TYPE_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                )}
              </Field>

              {referrerType === 'employee' && (
                <Field label="Departments" error={typeof errors.department_ids?.message === 'string' ? errors.department_ids.message : undefined}>
                  {(fp) => (
                    <Controller
                      name="department_ids"
                      control={control}
                      render={({ field }) => (
                        <MultiSelect
                          {...fp}
                          placeholder={deptLoading ? 'Loading…' : 'Select departments'}
                          options={deptMultiOpts}
                          value={(field.value ?? []).map(String)}
                          onChange={(vals) => field.onChange(vals.map(Number))}
                          disabled={deptLoading}
                          invalid={!!errors.department_ids}
                        />
                      )}
                    />
                  )}
                </Field>
              )}
            </div>

            {/* Scope + commission */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--vn-space-3)' }}>
              <Field label="Scope" required error={errors.scope?.message}>
                {(fp) => (
                  <Controller
                    name="scope"
                    control={control}
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger {...fp} invalid={!!errors.scope}>
                          <SelectValue placeholder="Select scope" />
                        </SelectTrigger>
                        <SelectContent>
                          {SCOPE_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                )}
              </Field>

              <Field label="Commission %" required error={errors.commission_percent?.message}>
                {(fp) => (
                  <Input
                    {...fp}
                    {...register('commission_percent', { valueAsNumber: true })}
                    type="number"
                    min={0}
                    max={100}
                    step={0.01}
                    placeholder="0–100"
                    invalid={!!errors.commission_percent}
                  />
                )}
              </Field>
            </div>

            {scope === 'product_group' && (
              <Field label="Product Group" required error={errors.product_group_id?.message}>
                {(fp) => (
                  <Controller
                    name="product_group_id"
                    control={control}
                    render={({ field }) => (
                      <Select
                        value={field.value != null ? String(field.value) : ''}
                        onValueChange={(v) => field.onChange(v ? Number(v) : undefined)}
                      >
                        <SelectTrigger {...fp} invalid={!!errors.product_group_id}>
                          <SelectValue placeholder={optionsLoading ? 'Loading…' : 'Select product group'} />
                        </SelectTrigger>
                        <SelectContent>
                          {productGroupOptions.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                )}
              </Field>
            )}

            {scope === 'product' && (
              <Field label="Product" required error={errors.product_id?.message}>
                {(fp) => (
                  <Controller
                    name="product_id"
                    control={control}
                    render={({ field }) => (
                      <Select
                        value={field.value != null ? String(field.value) : ''}
                        onValueChange={(v) => field.onChange(v ? Number(v) : undefined)}
                      >
                        <SelectTrigger {...fp} invalid={!!errors.product_id}>
                          <SelectValue placeholder={optionsLoading ? 'Loading…' : 'Select product'} />
                        </SelectTrigger>
                        <SelectContent>
                          {productOptions.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                )}
              </Field>
            )}

            {/* Date range + priority */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--vn-space-3)' }}>
              <Field label="Effective Date" required error={errors.effective_date?.message}>
                {(fp) => (
                  <Controller
                    control={control}
                    name="effective_date"
                    render={({ field }) => (
                      <DateInput
                        {...fp}
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                        invalid={!!errors.effective_date}
                      />
                    )}
                  />
                )}
              </Field>

              <Field label="Expiry Date" error={errors.expired_date?.message}>
                {(fp) => (
                  <Controller
                    control={control}
                    name="expired_date"
                    render={({ field }) => (
                      <DateInput
                        {...fp}
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      />
                    )}
                  />
                )}
              </Field>
            </div>

            <Field label="Priority" error={errors.priority?.message}>
              {(fp) => (
                <Input
                  {...fp}
                  {...register('priority', { valueAsNumber: true })}
                  type="number"
                  placeholder="0"
                  invalid={!!errors.priority}
                />
              )}
            </Field>

            <Field label="Description" error={errors.description?.message}>
              {(fp) => (
                <Textarea
                  {...fp}
                  {...register('description')}
                  rows={3}
                  placeholder="Optional description"
                />
              )}
            </Field>

            {submitError && (
              <Alert tone="danger" title={submitError} />
            )}
          </form>
        </DrawerBody>

        <DrawerFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button form="policy-form" type="submit" loading={isPending}>
            {isEdit ? 'Save changes' : 'Create policy'}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

